import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { Form } from '@edx/paragon';
import { ReactComponent as RadioIcon } from '../../profile/assets/radio-button.svg';
import { ReactComponent as RadioEmptyIcon } from '../../profile/assets/radio-button-empty.svg';

import messages from './Country.messages';

// Components
import FormControls from './elements/FormControls';
import EditableItemHeader from './elements/EditableItemHeader';
import EmptyContent from './elements/EmptyContent';
import SwitchContent from './elements/SwitchContent';
import CustomSelect from '../components/CustomSelect/CustomSelect';

// Selectors
import { countrySelector } from '../data/selectors';

const Country = ({
  formId,
  country,
  visibilityCountry,
  saveState,
  error,
  intl,
  sortedCountries,
  countryMessages,
  changeHandler,
  submitHandler,
  closeHandler,
  openHandler,
  changeData,
  saveData,
  onSaveComplete,
  editMode: initialEditMode,
}) => {
  const [editMode, setEditMode] = useState(changeData ? 'editing' : initialEditMode);
  const [selectedCountry, setSelectedCountry] = useState(country || '');

  useEffect(() => {
    setSelectedCountry(country || '');
  }, [country]);

  useEffect(() => {
    if (changeData) {
      setEditMode('editing');
    } else {
      handleClose();
    }
  }, [changeData]);

  useEffect(() => {
    if (saveData) {
      handleSubmit();
    }
  }, [saveData]);

  const handleChange = value => {
    setSelectedCountry(value);
    changeHandler(formId, value);
  };

  const handleSubmit = e => {
    // Проверяем, есть ли объект события
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    submitHandler(formId);
    if (onSaveComplete) {
      onSaveComplete(); // Сбрасываем состояние saveData в родительском компоненте
    }
  };

  const handleClose = () => {
    closeHandler(formId);
    setEditMode('static');
  };

  const handleOpen = () => {
    openHandler(formId);
    setEditMode('editing');
  };

  const countryOptions = [
    {
      value: '',
      label: '——',
      displayLabel: intl.formatMessage(messages['profile.country.option.default']),
    },
    ...sortedCountries.map(({ code, name }) => ({
      value: code,
      label: name,
      displayLabel: name,
    })),
  ];

  const renderOption = (option, currentValue) => {
    const isSelected = option.value === currentValue;
    const Icon = isSelected ? RadioIcon : RadioEmptyIcon;

    return (
      <>
        <Icon className="tw-w-4 tw-h-4 tw-min-w-4 tw-min-h-4" />
        <span className={option.value === '' ? 'tw-text-neutral-500' : ''}>{option.label}</span>
      </>
    );
  };

  return (
    <SwitchContent
      className="tw-mb-10"
      expression={editMode}
      cases={{
        editing: (
          <div role="dialog" aria-labelledby={`${formId}-label`}>
            <form onSubmit={handleSubmit}>
              <Form.Group controlId={formId} isInvalid={error !== null} className="tw-mb-0">
                <p className="!tw-mb-2 tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[100%]">
                  {intl.formatMessage(messages['profile.country.label'])}
                </p>
                <p className="tw-text-[14px] tw-text-placeholder tw-font-medium !tw-mb-[14px] tw-leading-[120%]">
                  {intl.formatMessage(messages['profile.country.placeholder'])}
                </p>
                <CustomSelect
                  options={countryOptions}
                  value={selectedCountry}
                  onChange={handleChange}
                  placeholder={intl.formatMessage(messages['profile.country.option.default'])}
                  renderOption={renderOption}
                  getOptionLabel={option => option.displayLabel}
                  getOptionValue={option => option.value}
                  enableSearch={false}
                />
                {error !== null && (
                  <Form.Control.Feedback hasIcon={false}>{error}</Form.Control.Feedback>
                )}
              </Form.Group>
              <div className="tw-hidden">
                <FormControls
                  visibilityId="visibilityCountry"
                  saveState={saveState}
                  visibility={visibilityCountry}
                  cancelHandler={handleClose}
                  changeHandler={handleChange}
                  saveData={saveData}
                />
              </div>
            </form>
          </div>
        ),
        editable: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.country.label'])}
              showEditButton
              onClickEdit={handleOpen}
              showVisibility={visibilityCountry !== null}
              visibility={visibilityCountry}
            />
            <p data-hj-suppress className="h5">
              {countryMessages[country]}
            </p>
          </>
        ),
        empty: (
          <>
            <EditableItemHeader content={intl.formatMessage(messages['profile.country.label'])} />
            <EmptyContent onClick={handleOpen}>
              {intl.formatMessage(messages['profile.country.empty'])}
            </EmptyContent>
          </>
        ),
        static: (
          <>
            <EditableItemHeader content={intl.formatMessage(messages['profile.country.label'])} />
            <p
              data-hj-suppress
              className="tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[150%]"
            >
              {countryMessages[country] || (
                <span className="tw-text-[14px] tw-text-placeholder tw-font-medium">
                  {intl.formatMessage(messages['profile.country.placeholder'])}
                </span>
              )}
            </p>
          </>
        ),
      }}
    />
  );
};

Country.propTypes = {
  formId: PropTypes.string.isRequired,
  country: PropTypes.string,
  visibilityCountry: PropTypes.oneOf(['private', 'all_users']),
  editMode: PropTypes.oneOf(['editing', 'editable', 'empty', 'static']),
  saveState: PropTypes.string,
  error: PropTypes.string,
  sortedCountries: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  countryMessages: PropTypes.objectOf(PropTypes.string).isRequired,
  changeHandler: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  closeHandler: PropTypes.func.isRequired,
  openHandler: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  changeData: PropTypes.bool,
  saveData: PropTypes.bool,
  onSaveComplete: PropTypes.func,
};

Country.defaultProps = {
  editMode: 'static',
  saveState: null,
  country: '',
  visibilityCountry: 'private',
  error: null,
  changeData: false,
  saveData: false,
  onSaveComplete: null,
};

export default connect(countrySelector, {})(injectIntl(Country));
